import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { service } from '@ember/service'
import { t } from 'ember-intl'
import { or } from 'ember-truth-helpers'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import writingLegendLyingDown from '@images/writing-legend-lying-down.png'
import mangoStanding from '@images/mango-standing.png'
import gizmoStanding from '@images/gizmo-standing.png'

interface Signature {
  Element: HTMLDivElement
  Args: {
    compact?: boolean
    title?: string
    body?: string
  }
  Blocks: {
    default: []
  }
}

/**
 * Intended for use in the reporting area to indicate there is no data. Similar to NoResultsMessage.
 */
export const NoDataMessage: TOC<Signature> = <template>
  <div data-test-no-data-message class="text-dusty-black-300 space-y-2 text-center" ...attributes>
    {{#if (has-block)}}
      {{yield}}
    {{else if @compact}}
      <NoDataMessageImage class="max-w-28" />
      <NoDataMessageBody
        class="font-bold"
        @body={{or @body (t "components.noDataMessage.bodyCompact" htmlSafe=true)}}
      />
    {{else}}
      <NoDataMessageImage class="max-w-xs" />
      <NoDataMessageTitle @title={{or @title (t "components.noDataMessage.title")}} />
      <NoDataMessageBody class="text-xl" @body={{or @body (t "components.noDataMessage.body" htmlSafe=true)}} />
    {{/if}}
  </div>
</template>

interface ImageSignature {
  Element: HTMLImageElement
}

interface ImageData {
  url: string
  alt: string
}

export class NoDataMessageImage extends Component<ImageSignature> {
  @service subscriptionType!: SubscriptionTypeService

  get image(): ImageData {
    const images: Record<SubscriptionType, ImageData> = {
      [SubscriptionType.Reading]: {
        url: gizmoStanding,
        alt: 'gizmo',
      },
      [SubscriptionType.Maths]: {
        url: mangoStanding,
        alt: 'mango the monkey',
      },
      [SubscriptionType.Writing]: {
        url: writingLegendLyingDown,
        alt: 'writing legend lying down',
      },
    }
    return images[this.subscriptionType.current ?? SubscriptionType.Reading]
  }

  <template>
    <img data-test-no-data-image class="mx-auto" src={{this.image.url}} alt={{this.image.alt}} ...attributes />
  </template>
}

interface TitleSignature {
  Element: HTMLDivElement
  Args: {
    title?: string
  }
}

export const NoDataMessageTitle: TOC<TitleSignature> = <template>
  <h2 data-test-no-data-title ...attributes>
    {{@title}}
  </h2>
</template>

interface BodySignature {
  Element: HTMLDivElement
  Args: {
    body?: string
  }
}

export const NoDataMessageBody: TOC<BodySignature> = <template>
  <p data-test-no-data-body ...attributes>
    {{@body}}
  </p>
</template>

export default NoDataMessage
